// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Pencil, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

interface SettingDialogProps {
  open: boolean;
  onClose: () => void;
  settingName: string;
  settingKey: string;
  description?: string;
  fieldType: "boolean" | "number" | "string" | "list" | "json" | "enum";
  enumValues?: string[];
  defaultValue: string;
  currentValue?: string;
  onSave: (value: string) => Promise<void>;
}

export function SettingDialog({
  open,
  onClose,
  settingName,
  settingKey,
  description,
  fieldType,
  enumValues,
  defaultValue,
  currentValue,
  onSave,
}: SettingDialogProps) {
  const { t } = useTranslation("back-office");
  const [value, setValue] = useState(currentValue ?? defaultValue);
  const [saving, setSaving] = useState(false);

  useEffect(
    function resetValueOnOpen() {
      if (open) {
        setValue(currentValue ?? defaultValue);
      }
    },
    [open, currentValue, defaultValue],
  );

  async function onSubmit() {
    setSaving(true);
    try {
      await onSave(value.trim());
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const isNumberInvalid = fieldType === "number" && value !== "" && Number.isNaN(Number(value));

  const enumOptions = useMemo(() => (enumValues ?? []).map((v) => ({ label: v, value: v })), [enumValues]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent id="dialog-edit-setting" className="sm:max-w-[37rem] p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane icon={Pencil} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("settings.list.editDialog.title")}</DialogTitle>
              {description && <DialogDescription>{description}</DialogDescription>}
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground text-xs">{t("settings.list.editDialog.nameLabel")}</Label>
                <p className="text-sm font-medium">{settingName}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">{t("settings.list.editDialog.keyLabel")}</Label>
                <p className="font-mono text-sm">{settingKey}</p>
              </div>
              <div className="space-y-2">
                <Label>{t("settings.list.editDialog.valueLabel")}</Label>
                {fieldType === "boolean" ? (
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={value === "true"}
                      onCheckedChange={(checked) => setValue(checked ? "true" : "false")}
                    />
                    <span className="text-sm">{value}</span>
                  </div>
                ) : fieldType === "enum" ? (
                  <VirtualSelectPopover
                    value={value}
                    onChange={setValue}
                    options={enumOptions}
                    placeholder={t("settings.list.editDialog.selectPlaceholder")}
                    searchPlaceholder={t("settings.list.editDialog.selectSearchPlaceholder")}
                    emptyText={t("settings.list.editDialog.noValues")}
                  />
                ) : fieldType === "json" ? (
                  <Textarea
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    rows={6}
                    className="font-mono text-sm max-h-[40vh]"
                  />
                ) : (
                  <Input
                    type={fieldType === "number" ? "number" : "text"}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                )}
                {isNumberInvalid && (
                  <p className="text-sm text-destructive">{t("settings.list.editDialog.invalidNumber")}</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("settings.list.editDialog.cancel")}
          </Button>
          <Button onClick={onSubmit} disabled={saving || isNumberInvalid}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t("settings.list.editDialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
